import axios from "axios";

class RestClient {
    /**
     * Get user info from session storage safely
     * SECURITY: Prevents errors from null/undefined sessionStorage items
     */
    getUserInfo() {
        try {
            const userStr = sessionStorage.getItem("user");
            return userStr ? JSON.parse(userStr) : null;
        } catch (error) {
            // SECURITY: Don't expose error details
            return null;
        }
    }

    /**
     * Get CSRF token from cookie (XSRF-TOKEN)
     * SECURITY: CSRF token from cookie set by backend
     */
    getCsrfTokenFromCookie() {
        try {
            const cookies = document.cookie.split(';');
            for (let cookie of cookies) {
                const [name, value] = cookie.trim().split('=');
                if (name === 'XSRF-TOKEN') {
                    return decodeURIComponent(value);
                }
            }
        } catch (error) {
            // Ignore cookie read errors
        }
        return null;
    }

    /**
     * Get CSRF token from session storage
     */
    getCsrfTokenFromStorage() {
        try {
            const tokenData = sessionStorage.getItem("csrfToken");
            if (tokenData) {
                const parsed = JSON.parse(tokenData);
                // Check if token is still valid (not expired)
                if (parsed.expiresAt && new Date(parsed.expiresAt) > new Date()) {
                    return parsed.token;
                }
                // Token expired, remove it
                sessionStorage.removeItem("csrfToken");
            }
        } catch (error) {
            // Ignore storage errors
        }
        return null;
    }

    /**
     * Store CSRF token in session storage with expiration
     */
    setCsrfToken(token) {
        try {
            if (token) {
                // Token expires in 15 minutes
                const expiresAt = new Date(Date.now() + 15 * 60 * 1000);
                sessionStorage.setItem("csrfToken", JSON.stringify({
                    token: token,
                    expiresAt: expiresAt.toISOString()
                }));
            }
        } catch (error) {
            // Ignore storage errors
        }
    }

    /**
     * Fetch CSRF token from backend
     * SECURITY: Fetches CSRF token from GET /api/csrf-token endpoint
     */
    async fetchCsrfToken() {
        try {
            const BASE_URL = process.env.REACT_APP_API_BASE_URL;
            const response = await axios.get(`${BASE_URL}/csrf-token`, {
                withCredentials: true // Include cookies
            });
            
            // Extract token from response
            const token = response?.data?.csrfToken;
            
            if (token) {
                this.setCsrfToken(token);
                return token;
            }
            
            // Also check cookie as fallback
            const cookieToken = this.getCsrfTokenFromCookie();
            if (cookieToken) {
                this.setCsrfToken(cookieToken);
                return cookieToken;
            }
            
            return null;
        } catch (error) {
            // If CSRF endpoint fails, try reading from cookie
            const cookieToken = this.getCsrfTokenFromCookie();
            if (cookieToken) {
                this.setCsrfToken(cookieToken);
                return cookieToken;
            }
            return null;
        }
    }

    /**
     * Get CSRF token - check storage and cookie first, then fetch if needed
     */
    async getCsrfTokenAsync() {
        // First check cookie (set by backend)
        let token = this.getCsrfTokenFromCookie();
        if (token) {
            this.setCsrfToken(token);
            return token;
        }

        // Then check session storage
        token = this.getCsrfTokenFromStorage();
        if (token) {
            return token;
        }

        // Finally, fetch from backend
        token = await this.fetchCsrfToken();
        return token;
    }

    /**
     * Check if error is CSRF related
     */
    isCsrfError(err) {
        const errorMessage = err?.response?.data?.message || err?.response?.data?.error || "";
        const status = err?.response?.status;
        
        return status === 403 && (
            errorMessage.toLowerCase().includes("csrf") ||
            errorMessage.toLowerCase().includes("token validation failed") ||
            errorMessage.toLowerCase().includes("token not found")
        );
    }

    /**
     * Handle unauthorized access
     * SECURITY: Centralized unauthorized handling
     */
    handleUnauthorized() {
        sessionStorage.clear();
        window.location.href = "/session/signin/step1";
    }

    /**
     * Get generic error message
     * SECURITY: Prevents information disclosure through error messages
     */
    getErrorMessage(err) {
        // Only return user-friendly messages, not system details
        if (err?.response?.data?.message) {
            return err.response.data.message;
        }
        return "An error occurred. Please try again.";
    }

    /**
     * Build headers with Authorization and CSRF token
     */
    async buildHeaders(includeCsrf = true) {
        const userInfo = this.getUserInfo();
        const headers = {
            Authorization: userInfo?.token ? "Bearer " + userInfo?.token : "",
        };

        // Add CSRF token for state-changing operations
        if (includeCsrf) {
            const csrfToken = await this.getCsrfTokenAsync();
            if (csrfToken) {
                headers["X-CSRF-Token"] = csrfToken;
            }
        }

        return headers;
    }

    async get(url, params = {}, options = {}) {
        const userInfo = this.getUserInfo();
        try {
            return await axios.get(`${url}`, {
                headers: {
                    Authorization: userInfo?.token ? "Bearer " + userInfo?.token : "",
                },
                params,
                withCredentials: true, // Include cookies for CSRF token
            });
        } catch (err) {
            if (err?.response?.status === 200) {
                return err?.response?.data;
            } else {
                if (err?.response?.data?.message === "Unauthorized" || err?.response?.status === 401) {
                    if (!options.skipAuthRedirect) {
                        this.handleUnauthorized();
                    }
                    return { status: false, message: "Session expired. Please login again." };
                } else {
                    return { status: false, message: this.getErrorMessage(err) };
                }
            }
        }
    }

    async post(url, params, retryOnCsrf = true, options = {}) {
        const lastUrl = url.substring(url.lastIndexOf("/") + 1);
        const userInfo = this.getUserInfo();

        let response = {};
        try {
            // Fetch CSRF token before authenticate/register (backend requires it)
            if (lastUrl === "authenticate" || lastUrl === "register") {
                // Fetch CSRF token first
                const csrfToken = await this.getCsrfTokenAsync();
                const headers = {
                    'Content-Type': 'application/json',
                };
                if (csrfToken) {
                    headers['X-CSRF-Token'] = csrfToken;
                }
                response = await axios.post(`${url}`, params, {
                    headers,
                    withCredentials: true
                });
                // After successful login/register, refresh CSRF token for subsequent requests
                if (response?.data?.status !== false) {
                    await this.fetchCsrfToken();
                }
            } else {
                const headers = await this.buildHeaders(true);
                response = await axios.post(`${url}`, params, {
                    headers,
                    withCredentials: true
                });
            }
            return response;
        } catch (err) {
            // Handle CSRF token errors
            if (this.isCsrfError(err) && retryOnCsrf) {
                // Clear old token and fetch new one
                sessionStorage.removeItem("csrfToken");
                const newToken = await this.fetchCsrfToken();
                if (newToken) {
                    // Retry the request once with new token
                    return this.post(url, params, false, options);
                }
            }

            if (err?.response?.status === 200) {
                return err?.response?.data;
            } else {
                if (err?.response?.data?.message === "Unauthorized" || err?.response?.status === 401) {
                    if (!options.skipAuthRedirect) {
                        this.handleUnauthorized();
                    }
                    return { status: false, message: "Session expired. Please login again." };
                } else {
                    return { status: false, message: this.getErrorMessage(err) };
                }
            }
        }
    }

    async put(url, params, retryOnCsrf = true) {
        const userInfo = this.getUserInfo();
        try {
            const headers = await this.buildHeaders(true);
            const response = await axios.put(`${url}`, params, {
                headers,
                withCredentials: true
            });
            return response;
        } catch (err) {
            // Handle CSRF token errors
            if (this.isCsrfError(err) && retryOnCsrf) {
                sessionStorage.removeItem("csrfToken");
                const newToken = await this.fetchCsrfToken();
                if (newToken) {
                    return this.put(url, params, false);
                }
            }

            if (err?.response?.status === 200) {
                return err?.response?.data;
            } else {
                if (err?.response?.data?.message === "Unauthorized" || err?.response?.status === 401) {
                    this.handleUnauthorized();
                    return { status: false, message: "Session expired. Please login again." };
                } else {
                    return { status: false, message: this.getErrorMessage(err) };
                }
            }
        }
    }

    async delete(url, params = {}, retryOnCsrf = true) {
        const userInfo = this.getUserInfo();
        try {
            const headers = await this.buildHeaders(true);
            const response = await axios.delete(`${url}`, {
                headers,
                withCredentials: true
            });
            return response;
        } catch (err) {
            // Handle CSRF token errors
            if (this.isCsrfError(err) && retryOnCsrf) {
                sessionStorage.removeItem("csrfToken");
                const newToken = await this.fetchCsrfToken();
                if (newToken) {
                    return this.delete(url, params, false);
                }
            }

            if (err?.response?.status === 200) {
                return err?.response?.data;
            } else {
                if (err?.response?.data?.message === "Unauthorized" || err?.response?.status === 401) {
                    this.handleUnauthorized();
                    return { status: false, message: "Session expired. Please login again." };
                } else {
                    return { status: false, message: this.getErrorMessage(err) };
                }
            }
        }
    }
}

export default RestClient;
